<div class="nacha-upload-step">
  <!-- Drag-and-drop zone -->
  <div
    class="file-uploader"
    [class.dragging]="isDragging"
    (dragover)="onDragOver($event)"
    (dragleave)="onDragLeave($event)"
    (drop)="onDrop($event)"
  >
    @if (!fileName) {
    <div class="upload-prompt">
      <mat-icon>upload_file</mat-icon>
      <p>Drag and drop your NACHA file here</p>
      <span>or</span>
      <input #fileInput type="file" accept=".txt" (change)="onFileSelected($event)" hidden />
      <button mat-raised-button color="primary" (click)="fileInput.click()">Browse Files</button>
      <p class="file-limit">Accepts .txt NACHA files — maximum 5MB</p>
    </div>
    } @else {
    <div class="file-info">
      <mat-icon class="status-icon">description</mat-icon>
      <div class="file-details">
        <p class="file-name">{{ fileName }}</p>
        @if (fileSummary) {
        <p class="file-stats">
          {{ fileSummary.entryCount }} entr{{ fileSummary.entryCount !== 1 ? 'ies' : 'y' }} detected
        </p>
        }
      </div>
      <button mat-icon-button (click)="clearFile()" [disabled]="isProcessing" class="clear-button" aria-label="Remove file">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    } @if (isProcessing) {
    <div class="processing">
      <mat-icon>hourglass_empty</mat-icon>
      <span>Reading file...</span>
    </div>
    }
  </div>

  <!-- Error message -->
  @if (uploadError) {
  <div class="error-message">
    <mat-icon>error</mat-icon>
    <span>{{ uploadError }}</span>
  </div>
  }

  <!-- Parse errors (non-fatal warnings) -->
  @if (parseErrors.length > 0) {
  <div class="parse-warnings">
    <div class="warnings-header">
      <mat-icon>warning</mat-icon>
      <strong>File validation warnings ({{ parseErrors.length }}):</strong>
    </div>
    <ul class="warnings-list">
      @for (err of parseErrors; track $index) {
      <li>{{ err }}</li>
      }
    </ul>
  </div>
  }

  <!-- File summary card -->
  @if (fileSummary && hasValidFile) {
  <div class="file-summary-card">
    <h4>
      <mat-icon>summarize</mat-icon>
      File Summary
    </h4>
    <div class="summary-grid">
      @if (fileSummary.originatorName) {
      <div class="summary-item">
        <span class="summary-label">Originator</span>
        <span class="summary-value">{{ fileSummary.originatorName }}</span>
      </div>
      } @if (fileSummary.destinationName) {
      <div class="summary-item">
        <span class="summary-label">Destination Bank</span>
        <span class="summary-value">{{ fileSummary.destinationName }}</span>
      </div>
      }
      <div class="summary-item">
        <span class="summary-label">Entry Count</span>
        <span class="summary-value">{{ fileSummary.entryCount }}</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">Batch Count</span>
        <span class="summary-value">{{ fileSummary.batchCount }}</span>
      </div>
      @if (fileSummary.secCodes.length > 0) {
      <div class="summary-item">
        <span class="summary-label">SEC Codes</span>
        <span class="summary-value">{{ fileSummary.secCodes.join(', ') }}</span>
      </div>
      } @if (fileSummary.totalDebitAmount > 0) {
      <div class="summary-item">
        <span class="summary-label">Total Debits</span>
        <span class="summary-value">{{ formatAmount(fileSummary.totalDebitAmount) }}</span>
      </div>
      } @if (fileSummary.totalCreditAmount > 0) {
      <div class="summary-item">
        <span class="summary-label">Total Credits</span>
        <span class="summary-value">{{ formatAmount(fileSummary.totalCreditAmount) }}</span>
      </div>
      } @if (fileSummary.fileCreationDate) {
      <div class="summary-item">
        <span class="summary-label">File Date</span>
        <span class="summary-value">{{ fileSummary.fileCreationDate }}</span>
      </div>
      }
    </div>
    <div class="summary-status" [class.valid]="fileSummary.isValid" [class.invalid]="!fileSummary.isValid">
      <mat-icon>{{ fileSummary.isValid ? 'check_circle' : 'warning' }}</mat-icon>
      <span>{{
        fileSummary.isValid
          ? 'File is valid — ready to review'
          : 'File has validation warnings — review before processing'
      }}</span>
    </div>
  </div>
  }

  <!-- Format tips -->
  @if (!fileName) {
  <div class="nacha-tips">
    <h4>NACHA File Format</h4>
    <ul>
      <li>Standard ACH fixed-width format (.txt), 94 characters per record</li>
      <li>Supports SEC codes: PPD, CCD, CTX, WEB, TEL</li>
      <li>Debit entries (codes 27, 37) are pre-selected for processing</li>
      <li>Credit entries can be optionally included on the review step</li>
      <li>Customer matching uses the Individual ID Number field</li>
      <li>Each row is processed as a one-time ACH payment — no tokens stored</li>
    </ul>
  </div>
  }
</div>
